import { useEffect, useState, useCallback } from "react";
import { Ban, ShieldCheck, KeyRound, MessagesSquare, Trash2, ChevronDown, ChevronUp, MicOff, Mic } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { roleFor } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { SectionTitle, Empty } from "@/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function UserRow({ u, reload, onViewChats, onResetPw, roles }) {
  const [showIps, setShowIps] = useState(false);
  const role = roleFor(u, roles);

  const setRole = async (mod_role) => {
    try { await api.post(`/admin/users/${u.id}/role`, { mod_role: mod_role || null }); toast.success("Rütbe güncellendi"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleBan = async () => {
    const banned = !u.banned;
    const ban_ip = banned ? window.confirm("IP adresleri de banlansın mı? (Tamam = evet, İptal = sadece hesap)") : false;
    try { await api.post(`/admin/users/${u.id}/ban`, { banned, ban_ip }); toast.success(banned ? "Kullanıcı banlandı" : "Ban kaldırıldı"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleChatBan = async () => {
    try { await api.post(`/admin/users/${u.id}/chat-ban`, { chat_banned: !u.chat_banned }); toast.success(!u.chat_banned ? "Sohbetten banlandı" : "Sohbet banı kaldırıldı"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`${u.username} kalıcı olarak silinsin mi?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Silindi"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-user-row" className={`cx-glass rounded-2xl p-4 sm:p-5 ${u.banned ? "border-red-500/40" : ""}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-100" data-testid="admin-user-username">{u.username}</span>
            {role && <RoleBadge role={role} size="xs" />}
            {u.banned && <span className="text-[10px] font-mono-cx px-2 py-0.5 rounded-md bg-red-950 text-red-300 border border-red-500/60">BANLI</span>}
            {u.chat_banned && <span data-testid="admin-user-chat-banned-badge" className="text-[10px] font-mono-cx px-2 py-0.5 rounded-md bg-amber-950 text-amber-300 border border-amber-500/60">SOHBET BANI</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">Kayıt: {fmtDate(u.created_at)} · Son IP: <span className="font-mono-cx text-slate-300" data-testid="admin-user-last-ip">{u.last_ip || "-"}</span> · {u.purchases} satın alma · {u.messages} mesaj</p>
        </div>
        {u.role !== "admin" && (
          <select data-testid="admin-user-role-select" value={u.mod_role || ""} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 px-2">
            <option value="">Rütbe yok</option>
            {Object.values(roles).sort((a, b) => (a.order || 0) - (b.order || 0)).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        )}
        <div className="flex gap-2 flex-wrap">
          <button data-testid="admin-user-view-chats" onClick={() => onViewChats(u)} className="h-9 px-3 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 inline-flex items-center gap-1 hover:border-cyan-500/50"><MessagesSquare className="h-3.5 w-3.5" /> Sohbetler</button>
          <button data-testid="admin-user-reset-password" onClick={() => onResetPw(u)} className="h-9 px-3 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 inline-flex items-center gap-1 hover:border-cyan-500/50"><KeyRound className="h-3.5 w-3.5" /> Şifre</button>
          {u.role !== "admin" && (
            <>
              <button data-testid="admin-user-chat-ban-toggle" onClick={toggleChatBan} className={`h-9 px-3 rounded-lg text-xs inline-flex items-center gap-1 border ${u.chat_banned ? "bg-emerald-950 border-emerald-500/50 text-emerald-300" : "bg-amber-950 border-amber-500/50 text-amber-300"}`}>
                {u.chat_banned ? <><Mic className="h-3.5 w-3.5" /> Sohbet Aç</> : <><MicOff className="h-3.5 w-3.5" /> Sohbetten Banla</>}
              </button>
              <button data-testid="admin-user-ban-toggle" onClick={toggleBan} className={`h-9 px-3 rounded-lg text-xs inline-flex items-center gap-1 border ${u.banned ? "bg-emerald-950 border-emerald-500/50 text-emerald-300" : "bg-red-950 border-red-500/50 text-red-300"}`}>
                {u.banned ? <><ShieldCheck className="h-3.5 w-3.5" /> Banı Kaldır</> : <><Ban className="h-3.5 w-3.5" /> Banla</>}
              </button>
              <button data-testid="admin-user-delete" onClick={remove} className="h-9 w-9 rounded-lg bg-slate-900 border border-slate-700 text-slate-400 hover:text-red-300 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
            </>
          )}
        </div>
      </div>
      <button data-testid="admin-user-toggle-ips" onClick={() => setShowIps(!showIps)} className="mt-3 text-[11px] font-mono-cx text-cyan-400 inline-flex items-center gap-1">
        {showIps ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} IP Geçmişi ({u.ips?.length || 0})
      </button>
      {showIps && (
        <div className="mt-2 rounded-xl bg-slate-950/70 border border-slate-800 p-3 max-h-40 overflow-y-auto text-xs font-mono-cx space-y-1" data-testid="admin-user-ip-list">
          {(u.ips || []).slice().reverse().map((e, i) => <div key={i} className="flex justify-between text-slate-400"><span className="text-slate-200">{e.ip}</span><span>{fmtDate(e.at)}</span></div>)}
          {(!u.ips || u.ips.length === 0) && <span className="text-slate-600">Kayıt yok</span>}
        </div>
      )}
    </div>
  );
}

export function AdminUsers({ onViewChats }) {
  const { roles } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([])), []);
  useEffect(() => { load(); }, [load]);

  const resetPw = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/users/${pwUser.id}/reset-password`, { new_password: newPw }); toast.success("Şifre sıfırlandı"); setPwUser(null); setNewPw(""); } catch (err) { toast.error(errMsg(err)); }
  };

  const filtered = (users || []).filter((u) => u.username.toLowerCase().includes(q.toLowerCase()) || (u.last_ip || "").includes(q));

  return (
    <div>
      <SectionTitle eyebrow="Üyeler" title="Kullanıcılar & IP Adresleri">
        <Input data-testid="admin-users-search" className="cx-input w-64" placeholder="Kullanıcı adı veya IP ara..." value={q} onChange={(e) => setQ(e.target.value)} />
      </SectionTitle>
      {users === null ? <div className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" /> : filtered.length === 0 ? <Empty text="Kullanıcı bulunamadı." /> : (
        <div className="space-y-3" data-testid="admin-users-list">
          {filtered.map((u) => <UserRow key={u.id} u={u} roles={roles} reload={load} onViewChats={onViewChats} onResetPw={setPwUser} />)}
        </div>
      )}

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100 sm:max-w-md" data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Şifre Sıfırla: {pwUser?.username}</DialogTitle>
            <DialogDescription className="text-slate-400">Kullanıcı şifresini unuttuysa buradan yeni şifre belirle ve ona ilet.</DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPw} className="space-y-3">
            <Input data-testid="admin-new-password-input" className="cx-input" placeholder="Yeni şifre" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={4} required />
            <Button data-testid="admin-reset-password-submit" type="submit" className="w-full cx-btn-primary rounded-xl h-11">Sıfırla</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
